import * as React from 'react';
import { Reducer, useCallback, useEffect, useReducer } from 'react';
import {
  GridRowsProp,
  GridColDef,
  GridEventListener,
  GridFilterModel,
  GridSortModel,
  DataGridPro,
} from '@mui/x-data-grid-pro';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import { TableContainer } from '@mui/material';
import { ContentBox } from 'components/ContentBox';

import styles from './styles.module.scss';
import { dataGridSortBuilder } from '../../../store/api/query-builders/data-grid/sort/data-grid-sort.builder';
import { dataGridFilterBuilder } from '../../../store/api/query-builders/data-grid/filter/data-grid-filter.builder';
import { dataGridPaginationBuilder } from '../../../store/api/query-builders/data-grid/pagination/data-grid-pagination.builder';
import { defaultTableState, TableActions, tableReducer } from './table.reducer';

type InputProps = {
  isLoading: boolean;
  columns: GridColDef[];
  rows: GridRowsProp;
  totalRows: number;
  onRowClick?: (id: string) => void;
  setQuery: (query: string) => void;
  getRowId?: (row: any) => string;
  initialState?: GridInitialStatePro | undefined;
};

export const DashboardDataGridTable = ({
  columns,
  rows,
  totalRows,
  onRowClick,
  isLoading,
  setQuery,
  getRowId,
  initialState,
}: InputProps) => {
  const [tableState, dispatch] = useReducer<Reducer<any, any>>(
    tableReducer,
    defaultTableState(initialState)
  );

  useEffect(() => {
    setQuery(
      [
        dataGridSortBuilder(tableState.sort),
        dataGridFilterBuilder(tableState.filter),
        dataGridPaginationBuilder(tableState.pagination),
      ]
        .filter((v) => !!v)
        .join('&')
    );
  }, [tableState]);

  const handlePageChange = (page: number) => {
    dispatch({ type: TableActions.SET_PAGINATION, payload: { page } });
  };

  const handlePageSizeChange = (pageSize: number) => {
    dispatch({ type: TableActions.SET_PAGINATION, payload: { pageSize } });
  };

  const handleSortModelChange = useCallback((sortModel: GridSortModel) => {
    dispatch({ type: TableActions.SET_SORT, payload: sortModel });
  }, []);

  const onFilterChange = React.useCallback((filterModel: GridFilterModel) => {
    dispatch({ type: TableActions.SET_FILTER, payload: filterModel });
  }, []);

  const handleRowClick: GridEventListener<'rowClick'> = (params) => {
    onRowClick && onRowClick(params.row.id);
  };

  return (
    <TableContainer component={ContentBox} style={{ width: '100%' }}>
      <DataGridPro
        disableSelectionOnClick
        // slots={{
        //   noRowsOverlay: () => <NoRowsOverlay />,
        // }}
        style={{ width: '100%', border: 'none' }}
        classes={{
          iconSeparator: styles.iconSeparator,
          columnHeader: styles.columnHeader,
          columnHeaderTitle: styles.columnHeaderTitle,
          cell: styles.cell,
        }}
        autoHeight={true}
        onRowClick={handleRowClick}
        getRowId={getRowId}
        rows={rows}
        columns={columns}
        loading={isLoading}
        onSortModelChange={handleSortModelChange}
        onFilterModelChange={onFilterChange}
        pagination={true}
        rowCount={totalRows}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        page={tableState.pagination.page}
        pageSize={tableState.pagination.pageSize}
        rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
        sortingMode="server"
        filterMode="server"
        paginationMode="server"
        disableColumnFilter={true}
        disableColumnMenu={true}
        initialState={initialState}
      />
    </TableContainer>
  );
};
